import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { capitalize } from '../../utils'
import {
  Checkbox,
  space,
  radius,
  color,
  usePrevious,
  fontWeight,
  device,
  fontSize,
} from '@ticketswap/solar'

const Container = styled.div`
  border-radius: ${radius.md};
  margin-bottom: ${space[16]};
  border-radius: ${radius.md};
  overflow: hidden;
`

const SeatingRow = styled.div`
  cursor: ${props => (props.isAvailable ? 'pointer' : 'unset')};
  position: relative;
  width: 100%;
  text-align: left;
  padding: ${space[16]};
  background-color: ${props =>
    props.checked
      ? color.sky
      : props.isAvailable
      ? color.stardust
      : color.stardustLight};
  display: grid;
  grid-gap: ${space[16]};
  grid-template-columns: ${space[24]} 6fr 1fr;
  margin-bottom: ${space[8]};
  border-radius: ${radius.md};

  @media ${device.tablet} {
    grid-gap: ${space[32]};
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`

const StyledCheckbox = styled(Checkbox)`
  pointer-events: none;
`

const Status = styled.div`
  white-space: nowrap;
  font-weight: ${fontWeight.semiBold};
  font-size: ${fontSize[14]};
  text-align: right;

  @media ${device.tablet} {
    font-size: ${fontSize[16]};
  }
`

const Seating = styled.div`
  opacity: ${props => (props.isAvailable ? 1 : 0.3)};
  pointer-events: none;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;

  @media ${device.tablet} {
    display: flex;
  }
`

const SeatingElement = styled.div`
  font-size: ${fontSize[16]};
  display: grid;
  grid-template-columns: 1fr 1fr;

  dt {
    font-weight: ${fontWeight.semiBold};
  }

  @media ${device.tablet} {
    display: flex;
    margin-right: ${space[32]};

    dt {
      margin-right: ${space[16]};
    }
  }
`

function SeatSelection({ t, listing, initialSelection, onChange }) {
  const [selection, setSelection] = React.useState(initialSelection)
  const previousSelection = usePrevious(selection)
  const isSelected = id => selection.indexOf(id) > -1

  const ticketsWithSeating = listing.tickets.edges

  React.useEffect(() => {
    if (
      typeof previousSelection !== 'undefined' &&
      selection !== previousSelection
    ) {
      onChange(selection)
    }
  }, [selection, previousSelection, onChange])

  return (
    <Container>
      {ticketsWithSeating.map(({ node }, i) => {
        const isAvailable = node.status === 'AVAILABLE'
        const itemClassName = 'seat-selection-item'
        const formattedSeating = Object.keys(node.seating)
          .filter(key => ['entrance', 'section', 'row', 'seat'].includes(key))
          .filter(key => Boolean(node.seating[key]))

        return (
          <SeatingRow
            data-testid={itemClassName}
            className={itemClassName}
            key={node.id}
            checked={isSelected(node.id)}
            isAvailable={isAvailable}
            onClick={e => {
              // TODO: This implementation is a bit of a hack and should be
              // refactored; Especially the `onClick`-handler on a div which is
              // an anti-pattern. But just wasn’t possible otherwise with the
              // current design.
              if (
                e.target.className &&
                typeof e.target.className === 'string' &&
                e.target.className.indexOf(itemClassName) !== -1 &&
                isAvailable
              ) {
                setSelection(
                  isSelected(node.id)
                    ? selection.filter(entry => entry !== node.id)
                    : [...selection, node.id]
                )
              }
            }}
          >
            <StyledCheckbox
              tabIndex="-1"
              on={isSelected(node.id)}
              label={`${capitalize(
                t('listing:purchaser.seatSelection.ticket', { number: i + 1 })
              )}`}
              hideLabel
            />

            <Seating isAvailable={isAvailable}>
              {formattedSeating.map((seatingElement, index) => (
                <SeatingElement key={`${seatingElement}-${index}`}>
                  <dt>{t(`common:generic.${seatingElement}`)}</dt>
                  <dd>{node.seating[seatingElement]}</dd>
                </SeatingElement>
              ))}
            </Seating>

            <Status>
              {node.status !== 'AVAILABLE' &&
                t(`common:generic.${node.status.toLowerCase()}`)}
            </Status>
          </SeatingRow>
        )
      })}
    </Container>
  )
}

SeatSelection.defaultProps = {
  initialSelection: [],
}

SeatSelection.propTypes = {
  t: PropTypes.func.isRequired,
  listing: PropTypes.shape({
    id: PropTypes.string,
    tickets: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string.isRequired,
            status: PropTypes.oneOf(['AVAILABLE', 'RESERVED', 'SOLD', 'LOCKED'])
              .isRequired,
            seating: PropTypes.shape({
              id: PropTypes.string,
              entrance: PropTypes.string,
              row: PropTypes.string,
              seat: PropTypes.string,
              section: PropTypes.string,
            }).isRequired,
          }).isRequired,
        })
      ),
    }).isRequired,
    price: PropTypes.object,
  }).isRequired,
  initialSelection: PropTypes.array,
  onChange: PropTypes.func,
}

export default SeatSelection
